import React, { useEffect, useMemo, useState } from 'react'
import {
  BundlePlanBoxStyled,
  BundlePlanBoxProduct,
  BundlePlanBoxProductRow,
  BundlePlanBoxTitle,
  BundlePlanBoxProductTitle,
  BundlePlanBoxBilledAnnually,
  BundlePlanCustomPricing,
  TrialNote,
} from 'Features/pricing/components/BundlePlanBox/BundlePlanBox.styled'
import {
  BundlePlanBoxButtonAndPrice,
  PlanBoxAmount,
  PlanBoxCurrency,
} from 'Features/pricing/components/PlanBox/PlanBox.styled'
import { Button, useResponsive } from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'
import { PLAN_TYPES, PlanTypes } from 'Types/PlansTypes'
import { Plan } from 'Features/pricing/types/Plan'
import { CurrenciesTypes } from 'Types/Currencies'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { getProductName } from 'Utils/productName'
import { CSS } from '@stitches/react'

import { ScheduleCallModal } from 'Features/ScheduleCallModal/ScheduleCallModal'
import { useGetPlan } from 'Features/pricing/hooks/useGetPlan'
import {
  CrossSellPlanBoxDiscount,
  CrossSellPlanBoxSeparator,
  PricingPlanBoxPriceRow,
} from 'Features/CrossSell/CrosSellPricing/CrossSellPlansBox/CrossSellPlansBox.styled'
import { CrossSellPricingConfirmationModal } from '../CrossSellPricingConfirmationModal/CrossSellPricingConfirmationModal'

import { showKiloOrMillion } from 'Utils/number'
import { MODIFY_PLANS_PAGES_TYPES } from 'Types/ModifyPlanPagesTypes'
import { botMitigationPlanSizes, paidMarketingPlanSizes } from 'Features/pricing/models/planSizes'
import { PlanTypesSorted } from 'Features/pricing/types/PlanTypesSorted'
import { useGetPlans } from 'Features/pricing/api/getPlans'
import { PRODUCT_TYPES, ProductTypesWithoutBundle } from 'Types/ProductTypes'
import { ModifyPlanBoxSummary } from 'Features/CrossSell/ModifyPlan/ModifyPlanBoxSummary/ModifyPlanBoxSummary'
import { useGetCheckoutSummary } from 'Features/CrossSell/CrosSellPricing/api/useGetCheckoutSummary'
import { CheckoutSummaryResponseData } from 'Types/CheckoutSummaryResponseType'
import { StringMap } from 'i18next'
import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { PAYMENT_METHOD } from 'Types/PaymentMethod'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'

type CrossSellPlansBoxProps = {
  paidMarketingPlan: PlanTypes
  botMitigationPlan: PlanTypes
  paidMarketingVisits: number
  botMitigationVisits: number
  plansDb: Array<Plan>
  currency: CurrenciesTypes
  productType?: ProductTypesWithoutBundle
  billingCycleMonths: number
  css?: CSS
  source: string
}

export const CrossSellPlansBox = ({
  paidMarketingVisits,
  botMitigationVisits,
  paidMarketingPlan,
  botMitigationPlan,
  currency,
  css,
  productType,
  billingCycleMonths,
  source,
}: CrossSellPlansBoxProps) => {
  const { t } = useTranslation('pricing')
  const [modalToShow, setModalToShow] = useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const plans = useGetPlans()
  const [pmPlan, setPmPlan] = useState<Plan>()
  const [bmPlan, setBmPlan] = useState<Plan>()
  const { findPlan, sortPlansByType } = useGetPlan()
  const { isLaptop } = useResponsive()
  const { isProductAllowedTrial } = useIsProductTrial()
  const isProductTrialAllowed = isProductAllowedTrial(productType)
  const queryData = useGetCheckoutSummary([pmPlan?.id || 0, bmPlan?.id || 0], isProductTrialAllowed)
  const isTrialSubscription = useIsTrialSubscription()
  const { paymentMethod } = useUserStore()
  const wireTransfer = paymentMethod === PAYMENT_METHOD.WIRE_TRANSFER
  const { data: details } = useGetPlansDetails()

  const addonProductIdsArray = details?.plans
    ?.filter((product) => product.type === 'Addon')
    .map((product) => product.id)

  const summaryData: CheckoutSummaryResponseData | undefined = useMemo(() => {
    if (queryData && queryData.data) {
      return queryData.data.data
    }
  }, [queryData])

  const plansByType: PlanTypesSorted = useMemo(() => {
    return sortPlansByType(plans.data || [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans.data])

  const isCustomPricing = useMemo(() => {
    return (
      paidMarketingPlan === PLAN_TYPES.ENTERPRISE ||
      botMitigationPlan === PLAN_TYPES.ENTERPRISE ||
      paidMarketingVisits > paidMarketingPlanSizes[4] ||
      botMitigationVisits > botMitigationPlanSizes[4]
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidMarketingVisits, botMitigationVisits, paidMarketingPlan, botMitigationPlan])

  const btnStyle = useMemo(
    () => ({
      width: isLaptop ? '85%' : '226px',
      maxWidth: isLaptop ? '85%' : '226px',
      height: isLaptop ? '30px' : '42px',
      maxHeight: '42px',
      marginTop: isLaptop ? '10px' : '20px',
    }),
    [isLaptop]
  )

  useEffect(() => {
    if (plansByType) {
      setPmPlan(
        findPlan(
          plansByType.paidMarketing,
          paidMarketingPlan,
          currency,
          PRODUCT_TYPES.PAID_MARKETING,
          paidMarketingVisits,
          billingCycleMonths
        )
      )
      setBmPlan(
        findPlan(
          plansByType.botMitigation,
          botMitigationPlan,
          currency,
          PRODUCT_TYPES.BOT_ZAPPING,
          botMitigationVisits,
          billingCycleMonths
        )
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paidMarketingVisits,
    botMitigationVisits,
    paidMarketingPlan,
    botMitigationPlan,
    plans.data,
    billingCycleMonths,
  ])
  const updatedPlans = [...(pmPlan ? [pmPlan] : []), ...(bmPlan ? [bmPlan] : [])]
  const priceToPay = Math.round(summaryData?.netAmountToPay || 0)

  const translate = (key: string, options?: StringMap): string => {
    const isTrialPerProduct = Boolean(isProductTrialAllowed && options?.trialPerProduct)
    return t(`cross-sell.cross_sale_pricing.${key}${isTrialPerProduct ? '_trial' : ''}`, options)
  }

  const handleClick = () =>
    isCustomPricing || wireTransfer ? setModalToShow(true) : setShowConfirmationModal(true)
  const getButtonText = () => {
    if (isCustomPricing || wireTransfer) return t('lets_talk')
    else if (isProductTrialAllowed) return translate('upgrade_plan', { trialPerProduct: true })
    else if (isTrialSubscription) return translate('add_to_trial')
    else return translate('upgrade_plan')
  }

  const amountWithCurrency = summaryData?.netAmountToPay
    ? getAmountWithCurrency(currency, Math.abs(Math.round(summaryData?.netAmountToPay || 0)))
    : 0

  const modalTitle = isTrialSubscription
    ? translate('conformation_modal_title_during_trial', {
        product: productType === PRODUCT_TYPES.BOT_ZAPPING ? 'Bot Mitigation' : 'Paid Marketing',
      })
    : translate('confirmation_modal_title', {
        trialPerProduct: true,
        product: getProductName(productType as ProductTypesWithoutBundle),
      })

  const descriptionText =
    isTrialSubscription && summaryData && summaryData?.netAmountToPay >= 0
      ? translate('charge_after_trial', {
          amount: amountWithCurrency,
        })
      : isTrialSubscription && summaryData && summaryData?.netAmountToPay < 0
      ? translate('credit_after_trial', {
          amount: amountWithCurrency,
        })
      : translate('confirmation_modal_description', {
          trialPerProduct: true,
          amount: getAmountWithCurrency(currency, Math.round(priceToPay)),
        })

  const showPlanBoxSummary =
    !wireTransfer &&
    !isCustomPricing &&
    pmPlan?.planType !== PLAN_TYPES.ENTERPRISE &&
    (pmPlan?.planType?.length || 0) > 0 &&
    bmPlan?.planType !== PLAN_TYPES.ENTERPRISE &&
    (bmPlan?.planType?.length || 0) > 0

  const paymentText: string = (() => {
    let text = ''
    if (summaryData && summaryData?.netAmountToPay >= 0) {
      text = translate('amount_to_pay_trial')
    } else if (summaryData && summaryData?.netAmountToPay < 0) {
      text = translate('amount_credited')
    } else if (summaryData) {
      text = translate('current_plan')
    }
    return text
  })()

  return (
    <>
      <BundlePlanBoxStyled css={css}>
        <BundlePlanBoxTitle>{translate('your_new_plan')}</BundlePlanBoxTitle>
        <BundlePlanBoxProduct>
          <BundlePlanBoxProductTitle>{t('bundles.paid_marketing')}</BundlePlanBoxProductTitle>
          <BundlePlanBoxProductRow>
            {t('bundles.plan_type', { plan: paidMarketingPlan })}
          </BundlePlanBoxProductRow>
          <BundlePlanBoxProductRow>
            {t('bundles.yearly_visits', {
              visits:
                paidMarketingVisits > paidMarketingPlanSizes[4]
                  ? t('paid_marketing.million')
                  : showKiloOrMillion(paidMarketingVisits * 1000),
            })}
          </BundlePlanBoxProductRow>
        </BundlePlanBoxProduct>
        <BundlePlanBoxProduct>
          <BundlePlanBoxProductTitle>{t('bundles.bot_mitigation')}</BundlePlanBoxProductTitle>
          <BundlePlanBoxProductRow>
            {t('bundles.plan_type', { plan: botMitigationPlan })}
          </BundlePlanBoxProductRow>
          <BundlePlanBoxProductRow>
            {t('bundles.yearly_visits', {
              visits:
                botMitigationVisits > botMitigationPlanSizes[4]
                  ? t('bot_mitigation.million')
                  : showKiloOrMillion(botMitigationVisits * 1000),
            })}
          </BundlePlanBoxProductRow>
        </BundlePlanBoxProduct>
        <CrossSellPlanBoxSeparator />
        <BundlePlanBoxButtonAndPrice>
          {!isCustomPricing ? (
            <>
              <CrossSellPlanBoxDiscount>{t('bundles.discount')}</CrossSellPlanBoxDiscount>
              <PricingPlanBoxPriceRow>
                <PlanBoxCurrency>{getAmountWithCurrency(currency, undefined)}</PlanBoxCurrency>
                <PlanBoxAmount>
                  {Math.round(Math.abs(summaryData?.netAmountToPay || 0))}
                </PlanBoxAmount>
              </PricingPlanBoxPriceRow>
              <BundlePlanBoxBilledAnnually>{paymentText}</BundlePlanBoxBilledAnnually>
            </>
          ) : (
            <BundlePlanCustomPricing> {t('bundles.custom_pricing')}</BundlePlanCustomPricing>
          )}
          {showPlanBoxSummary && <ModifyPlanBoxSummary isCrossSell plans={updatedPlans} />}
          <Button onClick={handleClick} css={btnStyle} text={getButtonText()} />
          {isProductTrialAllowed && (
            <TrialNote style={{ marginTop: '10px' }}>{translate('trial_note')}</TrialNote>
          )}
        </BundlePlanBoxButtonAndPrice>
      </BundlePlanBoxStyled>
      {showConfirmationModal && (
        <CrossSellPricingConfirmationModal
          pageType={MODIFY_PLANS_PAGES_TYPES.CROSS_SELL}
          title={modalTitle}
          description={descriptionText}
          plans={[pmPlan?.id || 0, bmPlan?.id || 0, ...(addonProductIdsArray as number[])]}
          onOpenChange={() => setShowConfirmationModal(false)}
          productType={productType}
          amountToPay={getAmountWithCurrency(currency, Math.round(priceToPay))}
          billingCycleInMonths={billingCycleMonths}
        />
      )}
      <ScheduleCallModal
        onOpenChange={() => setModalToShow(false)}
        isOpen={modalToShow}
        source={source}
      />
    </>
  )
}
